<template>
    <div data-test-view="reviews">
        <l-module-access
            :module="'reviews'"
            :module-access="team.moduleAccess"
            :title="$t(`adminSettings.reviewsHeadlineTitle`)"
        ></l-module-access>

        <hr />

        <l-headline
            :title="$t(`adminSettings.customizationHeadlineTitle`)"
            :sub-title="$t(`adminSettings.customizationHeadlineSubtitle`)"
        ></l-headline>

        <label class="color-dark" for="settings-indirectReportsVisible-check">
            <input
                id="settings-indirectReportsVisible-check"
                v-model="team.settings.indirectReportsVisible"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-reviews-indirect-reports-visible"
            />
            {{ $t(`adminSettings.reviewsIndirectReportsVisibleLabel`) }}
            <span
                v-b-popover.hover="{
                    content: $t(`adminSettings.reviewsIndirectReportsVisiblePopover`),
                }"
                class="l-default-margin"
            >
                <i class="fal fa-question-circle"></i>
            </span>
        </label>
        <br />
        <label class="color-dark" for="settings-adminsCanAccessAllReviews-check">
            <input
                id="settings-adminsCanAccessAllReviews-check"
                v-model="team.settings.adminsCanAccessAllReviews"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-reviews-admins-access-all"
            />
            {{ $t(`adminSettings.reviewsAdminsCanAccessAllReviewsLabel`) }}
            <span
                v-b-popover.hover="{
                    content: $t(`adminSettings.reviewsAdminsCanAccessAllReviewsPopover`),
                }"
                class="l-default-margin"
            >
                <i class="fal fa-question-circle"></i>
            </span>
        </label>
        <br />
        <label class="color-dark" for="settings-adminsCanCalibrate-check">
            <input
                id="settings-adminsCanCalibrate-check"
                v-model="team.settings.adminsCanCalibrate"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-reviews-calibrate"
            />
            {{ $t(`adminSettings.reviewsAdminsCanCalibrateLabel`) }}
            <span
                v-b-popover.hover="{
                    content: $t(`adminSettings.reviewsAdminsCanCalibrateHelp`),
                }"
                class="l-default-margin"
            >
                <i class="fal fa-question-circle"></i>
            </span>
        </label>
        <br />
        <label class="color-dark" for="settings-managerAnalyticsEnabled-check">
            <input
                id="settings-managerAnalyticsEnabled-check"
                v-model="team.settings.managerAnalyticsEnabled"
                type="checkbox"
                :disabled="!rights.A_PERM"
                data-test-id="team-settings-reviews-manager-analytics"
            />
            {{ $t(`adminSettings.reviewsManagerAnalyticsEnabledLabel`) }}
            <span
                v-b-popover.hover="{
                    content: $t(`adminSettings.reviewsManagerAnalyticsEnabledPopover`),
                }"
                class="l-default-margin"
            >
                <i class="fal fa-question-circle"></i>
            </span>
        </label>

        <hr />

        <div v-show="updateSuccess">
            <div
                class="alert alert-success"
                data-test-id="team-settings-reviews-settings-update-success"
            >
                {{ $t(`adminSettings.reviewsUpdateSuccess`) }}
            </div>
        </div>
        <div v-show="updateError">
            <div
                class="alert alert-danger"
                data-test-id="team-settings-reviews-settings-update-error"
            >
                {{ $t(`adminSettings.reviewsUpdateError`) }}
            </div>
        </div>
        <div class="row">
            <div class="col-md-offset-8 col-md-4">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    :disabled="!rights.A_PERM"
                    data-test-id="team-settings-reviews-settings-update"
                    @click="() => updateGlobalTeam({ scope: 'settingsReview' })"
                >
                    {{ $t(`adminSettings.reviewsUpdateBtn`) }}
                </button>
            </div>
        </div>

        <hr />

        <!-- REVIEW SCALE -->
        <l-headline
            :title="$t(`adminSettings.reviewsScaleHeadlineTitle`)"
            :sub-title="$t(`adminSettings.reviewsScaleHeadlineSubtitle`)"
        ></l-headline>

        <div class="row">
            <div class="col-md-2" style="margin-top: 9px;">
                <l-select
                    :label="$t(`adminSettings.reviewsScaleSelectLabel`)"
                    :options="
                        reviewScaleOptions.map(option => ({
                            value: option,
                            text: $t(`adminSettings.reviewsScaleOption_${option}`),
                        }))
                    "
                    :value="team.reviewScale.type"
                    data-test-id="team-settings-reviews-scale-type"
                    @change="e => changeReviewScale(e.target.value)"
                />
            </div>
            <div class="col-md-10">
                <div
                    v-for="item in ['1', '2', '3', '4', '5', '6', '7'].filter(
                        i => team.reviewScale.type >= i && team.reviewScale.type !== '100',
                    )"
                    :key="item"
                    class="form-group label-floating"
                >
                    <l-input-text
                        ref="input-scale"
                        v-model="team.reviewScale[`r${item}`].label"
                        type="text"
                        :label="$t(`adminSettings.reviewsScaleLabel${item}`)"
                        :placeholder="''"
                        :data-test-id="`team-settings-reviews-scale-r${item}-label`"
                    />
                </div>
                <!-- continues-->
                <div v-if="team.reviewScale.type == '100'" class="form-group label-floating">
                    <l-input-text
                        ref="input-r1"
                        v-model="team.reviewScale.r1.label"
                        type="text"
                        :label="$t(`adminSettings.reviewsScaleLabelLower`)"
                        :placeholder="''"
                        data-test-id="team-settings-reviews-scale-r1-label"
                    />
                </div>
                <div v-if="team.reviewScale.type == '100'" class="form-group label-floating">
                    <l-input-text
                        ref="input-r5"
                        v-model="team.reviewScale.r5.label"
                        type="text"
                        :label="$t(`adminSettings.reviewsScaleLabelUpper`)"
                        :placeholder="''"
                        data-test-id="team-settings-reviews-scale-r5-label"
                    />
                </div>
                <div v-if="team.reviewScale.type != '100'" style="margin-top: -20px">
                    <div class="hr-small"></div>
                    <input
                        v-model="team.reviewScale.hideNumbers"
                        type="checkbox"
                        data-test-id="team-settings-reviews-scale-hide-numbers"
                    />
                    <l-text :text="$t(`reviews.admin.labelScaleHideNumbers`)" />
                </div>
            </div>
        </div>

        <br />

        <div v-show="updateScaleSuccess">
            <hr />
            <l-text-alert
                type="success"
                :text="$t(`adminSettings.reviewsScaleUpdateSuccess`)"
                data-test-id="team-settings-reviews-scale-update-success"
            />
        </div>
        <div v-show="updateScaleError">
            <hr />
            <l-text-alert
                type="warning"
                :text="$t(`adminSettings.reviewsScaleUpdateError`)"
                data-test-id="team-settings-reviews-scale-update-error"
            />
        </div>
        <div class="row">
            <div class="col-md-6 col-md-offset-2" style="margin-top: 15px">
                <input
                    v-model="team.overrideTemplateScale"
                    type="checkbox"
                    data-test-id="team-settings-reviews-scale-override-template"
                />
                <l-text :text="$t(`adminSettings.reviewsScaleOverrideTemplateScaleLabel`)" />
                <span
                    v-b-popover.hover="{
                        content: $t(`adminSettings.reviewsScaleOverrideTemplateScalePopover`),
                    }"
                    class="l-default-margin"
                >
                    <i class="fal fa-question-circle"></i>
                </span>
            </div>
            <div class="col-md-4">
                <button
                    class="btn btn-primary btn-raised btn-block"
                    data-test-id="team-settings-reviews-scale-update"
                    @click="() => updateGlobalTeam({ scope: 'reviewScale' })"
                >
                    {{ $t(`adminSettings.reviewsScaleUpdateBtn`) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LSelect from "@/components/LSelect";
import LText from "@/components/LText";
import LTextAlert from "@/components/LTextAlert";
import LModuleAccess from "@/components/LModuleAccess";

export default {
    components: {
        LHeadline,
        LInputText,
        LSelect,
        LText,
        LTextAlert,
        LModuleAccess,
    },
    data() {
        return {
            team: {},
            updateSuccess: false,
            updateError: false,
            reviewScaleOptions: ["2", "3", "4", "5", "6", "7", "100"],
            updateScaleSuccess: false,
            updateScaleError: false,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            getTeamInfo: "teams/current/info",
        }),
    },
    created() {
        // Cloning the information from the store so we can modify it before updating the store,
        // when the user clicks on the update button.
        //
        this.team = this.getTeamInfo();
        this.team.overrideTemplateScale = false;
    },
    methods: {
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .flat()
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },
        changeReviewScale(type) {
            let reviewScale;
            switch (type) {
                case "2":
                    reviewScale = {
                        type: "2",
                        r1: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type2_R1`),
                            amount: 0,
                        },
                        r2: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type2_R2`),
                            amount: 100,
                        },
                        r3: { amount: 999 },
                        r4: { amount: 999 },
                        r5: { amount: 999 },
                        r6: { amount: 999 },
                        r7: { amount: 999 },
                    };
                    break;
                case "3":
                    reviewScale = {
                        type: "3",
                        r1: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type3_R1`),
                            amount: 0,
                        },
                        r2: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type3_R2`),
                            amount: 50,
                        },
                        r3: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type3_R3`),
                            amount: 100,
                        },
                        r4: { amount: 999 },
                        r5: { amount: 999 },
                        r6: { amount: 999 },
                        r7: { amount: 999 },
                    };
                    break;
                case "4":
                    reviewScale = {
                        type: "4",
                        r1: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type4_R1`),
                            amount: 0,
                        },
                        r2: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type4_R2`),
                            amount: 33,
                        },
                        r3: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type4_R3`),
                            amount: 66,
                        },
                        r4: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type4_R4`),
                            amount: 100,
                        },
                        r5: { amount: 999 },
                        r6: { amount: 999 },
                        r7: { amount: 999 },
                    };
                    break;
                case "5":
                    reviewScale = {
                        type: "5",
                        r1: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type5_R1`),
                            amount: 0,
                        },
                        r2: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type5_R2`),
                            amount: 25,
                        },
                        r3: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type5_R3`),
                            amount: 50,
                        },
                        r4: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type5_R4`),
                            amount: 75,
                        },
                        r5: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type5_R5`),
                            amount: 100,
                        },
                        r6: { amount: 999 },
                        r7: { amount: 999 },
                    };
                    break;
                case "6":
                    reviewScale = {
                        type: "6",
                        r1: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type6_R1`),
                            amount: 0,
                        },
                        r2: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type6_R2`),
                            amount: 20,
                        },
                        r3: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type6_R3`),
                            amount: 40,
                        },
                        r4: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type6_R4`),
                            amount: 60,
                        },
                        r5: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type6_R5`),
                            amount: 80,
                        },
                        r6: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type6_R6`),
                            amount: 100,
                        },
                        r7: { amount: 999 },
                    };
                    break;
                case "7":
                    reviewScale = {
                        type: "7",
                        r1: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type7_R1`),
                            amount: 0,
                        },
                        r2: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type7_R2`),
                            amount: 16,
                        },
                        r3: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type7_R3`),
                            amount: 33,
                        },
                        r4: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type7_R4`),
                            amount: 50,
                        },
                        r5: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type7_R5`),
                            amount: 67,
                        },
                        r6: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type7_R6`),
                            amount: 83,
                        },
                        r7: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type7_R7`),
                            amount: 100,
                        },
                    };
                    break;
                case "100":
                    reviewScale = {
                        type: "100",
                        r1: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type100_R1`),
                            amount: 0,
                        },
                        r2: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type100_R2`),
                            amount: 25,
                        },
                        r3: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type100_R3`),
                            amount: 50,
                        },
                        r4: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type100_R4`),
                            amount: 75,
                        },
                        r5: {
                            label: this.$t(`adminSettings.reviewsScaleLabel_Type100_R5`),
                            amount: 100,
                        },
                    };
                    break;
                default:
                    reviewScale = this.team.reviewScale;
                    break;
            }

            this.team.reviewScale = reviewScale;
        },
        updateGlobalTeam({ scope }) {
            if (scope === "settingsReview") {
                this.updateSuccess = false;
                this.updateError = false;

                this.$store
                    .dispatch({
                        type: "teams/current/update",
                        payload: {
                            scope: "settingsReviews",
                            update: {
                                indirectReportsVisible: this.team.settings.indirectReportsVisible,
                                adminsCanAccessAllReviews: this.team.settings
                                    .adminsCanAccessAllReviews,
                                adminsCanCalibrate: this.team.settings.adminsCanCalibrate,
                                managerAnalyticsEnabled: this.team.settings.managerAnalyticsEnabled,
                            },
                        },
                    })
                    .then(() => {
                        this.updateSuccess = true;
                    })
                    .catch(() => {
                        this.updateError = true;
                    });
            } else if (scope === "reviewScale") {
                this.updateScaleSuccess = false;
                this.updateScaleError = false;

                if (!this.isValid()) {
                    return;
                }

                this.$store
                    .dispatch({
                        type: "teams/current/update",
                        payload: {
                            scope: "reviewScale",
                            update: {
                                reviewScale: this.team.reviewScale,
                                overrideTemplateScale: this.team.overrideTemplateScale,
                            },
                        },
                    })
                    .then(() => {
                        this.updateScaleSuccess = true;
                    })
                    .catch(() => {
                        this.updateScaleError = true;
                    });
            }
        },
    },
};
</script>

<style scoped></style>
